import { useMutation } from '@apollo/react-hooks';
import { InsertEventLogMutation } from '@msales/shared/graphql/mutations/event/insertEvent';
import { useOfflineModeContext } from '../context';
import localforage from 'localforage';
import moment from 'moment';
import { useNetworkState } from 'react-use';
import { checkGraphQLHealth } from '../utils/graphqlHealthCheck';
import { reportEventLogError } from '../utils/sentryHelpers';
import getCoords, { getBackgroundCoords } from '../utils/getCoords';
import { withTimeout } from '../utils/withTimeout';

const eventLogsStore = localforage.createInstance({
  name: 'eventLogsDB',
  storeName: 'eventLogs',
});

const MAX_EVENT_AGE_DAYS = 30;
const MAX_EVENT_QUEUE_SIZE = 500;
const LOCATION_TIMEOUT_MS = 12000;
const MUTATION_TIMEOUT_MS = 15000;
const STORAGE_TIMEOUT_MS = 10000;
const EMPTY_LOCATION = { lng: null, lat: null };

// All hook instances share the same event store and server timeline.
let eventLogWork = Promise.resolve();
const serializeEventLog = (operation) => {
  const result = eventLogWork.then(operation);
  eventLogWork = result.catch(() => {});
  return result;
};

const readOfflineLogs = async () =>
  (await withTimeout(eventLogsStore.getItem('offlineLogs'), STORAGE_TIMEOUT_MS, () => new Error('Event log storage read timed out'))) || [];
const writeOfflineLogs = (logs) =>
  withTimeout(eventLogsStore.setItem('offlineLogs', logs), STORAGE_TIMEOUT_MS, () => new Error('Event log storage write timed out'));
const clearOfflineLogs = () =>
  withTimeout(eventLogsStore.removeItem('offlineLogs'), STORAGE_TIMEOUT_MS, () => new Error('Event log storage write timed out'));

const MAX_SERVER_ERROR_ATTEMPTS = 5;
const PERMANENT_ERROR_CODES = ['BAD_USER_INPUT', 'GRAPHQL_VALIDATION_FAILED', 'GRAPHQL_PARSE_FAILED'];
const getServerErrors = (error) => [
  ...(error?.graphQLErrors || []),
  ...(error?.networkError?.result?.errors || []),
];
const isServerRejection = (error) => {
  const errors = getServerErrors(error);
  return errors.length > 0 &&
    errors.every((item) => PERMANENT_ERROR_CODES.includes(item?.extensions?.code));
};

export const useInsertEventLog = () => {
  const [insertEventLog] = useMutation(InsertEventLogMutation);
  const { networkState } = useOfflineModeContext();
  const { networkState: realNetworkState } = useNetworkState();

  const fetchLocation = async () => {
    let coords = await getBackgroundCoords();

    if (!coords?.latitude || !coords?.longitude) {
      coords = await getCoords(false);
    }

    if (coords?.latitude && coords?.longitude) {
      return {
        lng: coords.longitude.toString(),
        lat: coords.latitude.toString(),
      };
    }
    return EMPTY_LOCATION;
  };

  const cleanupOldEvents = async (events) => {
    if (!events || events.length === 0) return events;

    const cutoffDate = moment()
      .subtract(MAX_EVENT_AGE_DAYS, 'days')
      .format('YYYY-MM-DD');

    let filtered = events.filter((event) => {
      if (!event.createdAt) return true;
      const eventDate = moment(event.createdAt, 'YYYY-MM-DD HH:mm:ss').format(
        'YYYY-MM-DD',
      );
      return eventDate >= cutoffDate;
    });

    if (filtered.length > MAX_EVENT_QUEUE_SIZE) {
      filtered = filtered.slice(-MAX_EVENT_QUEUE_SIZE);
    }

    return filtered;
  };

  const sendEventLog = async (cond) => {
    const response = await withTimeout(
      insertEventLog({ variables: { cond } }), MUTATION_TIMEOUT_MS, () => new Error('Event log request timed out'));
    if (response?.errors?.length || !response?.data) {
      const error = new Error('Event log rejected by server');
      error.graphQLErrors = response?.errors || [];
      throw error;
    }
    return response;
  };

  const pushOfflineLogs = async () => {
    const offlineLogs = await readOfflineLogs();

    if (offlineLogs.length === 0) return true;

    const isServerReachable = await checkGraphQLHealth();
    if (!isServerReachable) {
      console.warn('GraphQL server unreachable, skipping offline log sync');
      return false;
    }

    for (let index = 0; index < offlineLogs.length; index += 1) {
      const { syncAttempts = 0, ...log } = offlineLogs[index];
      try {
        await sendEventLog(log);
      } catch (error) {
        console.error('Failed to sync log:', error, log);
        reportEventLogError(error, log, { syncAttempt: true });
        if (isServerRejection(error)) continue;
        const answeredWithError = getServerErrors(error).length > 0;
        if (answeredWithError && syncAttempts + 1 >= MAX_SERVER_ERROR_ATTEMPTS) continue;
        // Later events must not overtake the first event still awaiting sync.
        const remainingLogs = await cleanupOldEvents([
          { ...log, syncAttempts: answeredWithError ? syncAttempts + 1 : syncAttempts },
          ...offlineLogs.slice(index + 1),
        ]);
        await writeOfflineLogs(remainingLogs);
        return false;
      }
    }

    await clearOfflineLogs();
    console.log('All offline logs successfully synced and removed.');
    return true;
  };
  const queueEventToOfflineStorage = async (input) => {
    const offlineLogs = await readOfflineLogs();
    const cleanedLogs = await cleanupOldEvents([...offlineLogs, input]);
    await writeOfflineLogs(cleanedLogs);
  };

  const sendEventOnline = async (input) => {
    try {
      const isServerReachable = await checkGraphQLHealth();
      if (!isServerReachable) {
        console.warn('GraphQL server unreachable, saving event locally');
        return null;
      }

      const flushed = await pushOfflineLogs();
      if (!flushed) return null;

      return await sendEventLog(input);
    } catch (error) {
      console.error('Failed to send event log, saving locally:', error);
      reportEventLogError(error, input, { isOnline: true, phase: 'insert' });
      return null;
    }
  };

  const insertEvent = ({
    eventCode,
    customer,
    document,
    documentTable,
    remark,
  }) => {
    // Capture occurrence time when called, before waiting for older events.
    const occurredAt = moment();
    const currentDate = occurredAt.format('YYYY-MM-DD');
    const createdAt = occurredAt.format('YYYY-MM-DD HH:mm:ss Z');

    return serializeEventLog(async () => {
      const { lng, lat } = await withTimeout(
        fetchLocation(), LOCATION_TIMEOUT_MS, () => new Error('Location lookup timed out')).catch(() => EMPTY_LOCATION);

      const input = {
        lng,
        lat,
        eventCode,
        customer,
        document,
        documentTable,
        remark,
        currentDate,
        createdAt,
      };
      const isOnline = networkState?.online || realNetworkState?.online;

      try {
        if (isOnline) {
          const response = await sendEventOnline(input);
          if (response) return response;
        } else {
          console.warn('Event saved locally due to offline status:', input);
        }

        await queueEventToOfflineStorage(input);
      } catch (error) {
        console.error('Error handling event log:', error);
        reportEventLogError(
          error,
          { eventCode, customer, document },
          { isOnline, phase: 'insert' },
        );
        throw error;
      }
    });
  };

  return {
    insertEvent,
    pushOfflineLogs: () => serializeEventLog(pushOfflineLogs),
  };
};
